/**
 * Admin service for superadmin panel operations.
 * Queries Hugo_ToolAccess, Hugo_ActivityLog, Hugo_Organizations collections.
 */
import { db } from '../firebase';
import {
  collection,
  getDocs,
  getDoc,
  setDoc,
  doc,
  query,
  orderBy,
  limit,
  onSnapshot,
  Timestamp,
} from 'firebase/firestore';
import { collections, logActivity } from '../utils/firestore';

/**
 * Get all tool access records (Hugo_ToolAccess collection).
 * Each document ID is a user UID, containing { tools: { toolSlug: { hasAccess, grantedAt, grantedBy } } }
 */
export async function getAllToolAccess() {
  const snapshot = await getDocs(collection(db, collections.toolAccess));
  return snapshot.docs.map((d) => ({ uid: d.id, ...d.data() }));
}

/**
 * Subscribe to all tool access records in real-time
 */
export function subscribeToAllToolAccess(callback) {
  return onSnapshot(collection(db, collections.toolAccess), (snapshot) => {
    const records = snapshot.docs.map((d) => ({ uid: d.id, ...d.data() }));
    callback(records);
  });
}

/**
 * Grant tool access for a user
 */
export async function grantToolAccess(uid, toolSlug, grantedBy, organizationId = null) {
  const docRef = doc(db, collections.toolAccess, uid);
  const docSnap = await getDoc(docRef);

  const existingData = docSnap.exists() ? docSnap.data() : { tools: {} };
  const toolAccess = {
    hasAccess: true,
    grantedAt: Timestamp.now(),
    grantedBy,
  };
  if (organizationId) {
    toolAccess.organizationId = organizationId;
  }
  const updatedTools = {
    ...existingData.tools,
    [toolSlug]: toolAccess,
  };

  await setDoc(docRef, { tools: updatedTools }, { merge: true });

  // Log activity
  await logActivity(grantedBy, 'grant_tool_access', toolSlug, {
    targetUser: uid,
    tool: toolSlug,
  });
}

/**
 * Revoke tool access for a user
 */
export async function revokeToolAccess(uid, toolSlug, revokedBy) {
  const docRef = doc(db, collections.toolAccess, uid);
  const docSnap = await getDoc(docRef);

  if (!docSnap.exists()) return;

  const existingData = docSnap.data();
  const updatedTools = {
    ...existingData.tools,
    [toolSlug]: {
      hasAccess: false,
      revokedAt: Timestamp.now(),
      revokedBy,
    },
  };

  await setDoc(docRef, { tools: updatedTools }, { merge: true });

  // Log activity
  await logActivity(revokedBy, 'revoke_tool_access', toolSlug, {
    targetUser: uid,
    tool: toolSlug,
  });
}

/**
 * Get recent activity log entries
 */
export async function getRecentActivity(maxEntries = 50) {
  const q = query(
    collection(db, collections.activityLog),
    orderBy('createdAt', 'desc'),
    limit(maxEntries)
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/**
 * Subscribe to recent activity in real-time
 */
export function subscribeToRecentActivity(callback, maxEntries = 50) {
  const q = query(
    collection(db, collections.activityLog),
    orderBy('createdAt', 'desc'),
    limit(maxEntries)
  );
  return onSnapshot(q, (snapshot) => {
    const entries = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
    callback(entries);
  }, (error) => {
    console.error('Activity log subscription error:', error.message);
    // Fall back to empty list on permission errors
    callback([]);
  });
}

/**
 * Available tools in the platform
 */
export const AVAILABLE_TOOLS = [
  { slug: 'drift-accumulation', name: 'Drift & Accumulation' },
];
